/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import Pagination from "../../../common/components/Pagination";
import Row from "../../../common/core/Row";
import Col from "../../../common/core/Col";
import Button from "../../../common/core/Button";
import Dropdown from "../../../common/core/Dropdown";
import DropdownToggle from "../../../common/core/DropdownToggle";
import DropdownMenu from "../../../common/core/DropdownMenu";
import DropdownItem from "../../../common/core/DropdownItem";
import Search from "./Search";
import CustomerData from "./CustomerData";
import Edit from "./Edit";
import NoResult from "../../../common/components/NoResult";
import Alert from "../../../common/core/Alert";
import Constants from "../../../common/utils/Constants";
import Permission from "../../../common/security/Permission";
import Rules from "../../../common/security/PermissionRules";

const Customers = (props) => {
  const {
    customers,
    collapsesOpen,
    toggleCollapses,
    hasData,
    toggleEditPage,
    showActive,
    handleClose,
    handleSave,
    handleEditCustomer,
    customer,
    searchTerm,
    handleSearchTerm,
    handleSearch,
    page,
    getPageData,
    size,
    totalCount,
    isLoading,
    methods,
    toggleAll,
    handleKeyPress,
    message,
    editCustomerInProgress,
    displaySize,
    toggleDropdown,
    dropdownOpen,
    handleSelectedSize,
    handleSelectedCompany,
    companyId,
    companyIds,
  } = props;

  const { AVAILABLE_COMPANIES } = Constants;

  return (
    <div className="contents">
      <h3 className="fw-bold mb-3">B2B Customers</h3>
      <div className="search-outer mb-4">
        <Search
          searchTerm={searchTerm}
          handleSearchTerm={handleSearchTerm}
          handleSearch={handleSearch}
          handleKeyPress={handleKeyPress}
          handleSelectedCompany={handleSelectedCompany}
          companyId={companyId}
          companyIds={companyIds}
        />
      </div>
      {
        message && message.type === "success" && (
          <Alert color={message.type} className="mt-2">
            {message.message}
          </Alert>
        )
      }
      <Permission
        allowed={Rules.Control.VIEW_CUSTOMERS_LIST}
      >
        {hasData && companyId ? (
          <>
            <div className="data-outer">
              <Row className="justify-content-between mb-4">
                <Col className="d-flex align-items-center">
                  <h5 className="fw-bold mb-0">
                    {AVAILABLE_COMPANIES[companyId]}
                    - Customer Accounts
                  </h5>
                </Col>
              </Row>
              <Row className="data-options mb-4">
                <Col md="auto">
                  <div className="show-items">
                    {totalCount >= size && (
                      <>
                        Show
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="dropdown-list mx-2">
                          <DropdownToggle caret color="white">{size}</DropdownToggle>
                          <DropdownMenu right>
                            {displaySize.map((display) => (
                              <>
                                {(display === 20 || totalCount >= display) && display && (
                                  <DropdownItem
                                    active={display === size}
                                    onClick={() => handleSelectedSize(display)}
                                  >
                                    {display}
                                  </DropdownItem>
                                )}
                              </>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      </>
                    )}
                    Showing
                    <span className="mx-1">{totalCount}</span>
                    {totalCount > 1 ? "Results" : "Result"}
                  </div>
                </Col>
                <Col md="auto" className="data-transfer">
                  {/* TODO: Import fucnctionality */}
                  {/* <Button color="secondary" className="me-2 btn-radius">
                  <i className="fal fa-file-plus me-2"> </i>
                  Import
                </Button> */}
                  {/* TODO: Download fucnctionality */}
                  {/* <Button color="primary" className="btn-radius">
                  <i className="fal fa-file-download me-2"> </i>
                  Download
                </Button> */}
                </Col>
              </Row>
              <div className="data-list-outer">
                <div className="data-header-outer">
                  <div className="data-list-header">
                    <div className="title-1">
                      <Button color="transparent" onClick={toggleAll}>
                        {Array.isArray(collapsesOpen) && collapsesOpen.length > 0
                          ? (
                            <>
                              Collapse All
                              <i className="fal fa-minus-square ms-2"> </i>
                            </>
                          )
                          : (
                            <>
                              Expand All
                              <i className="fal fa-plus-square ms-2"> </i>
                            </>
                          )}
                      </Button>
                    </div>
                    <div className="title-2">
                      <Button color="transparent">
                        Customer Account
                      </Button>
                    </div>
                    <div className="title-3">
                      <Button color="transparent">
                        Invoice Account
                      </Button>
                    </div>
                    <div className="title-4">
                      <Button color="transparent">
                        Name
                      </Button>
                    </div>
                  </div>
                </div>
                <CustomerData
                  customers={customers}
                  collapsesOpen={collapsesOpen}
                  toggleCollapses={toggleCollapses}
                  toggleEditPage={toggleEditPage}
                  isLoading={isLoading}
                />
                {showActive ? (
                  <>
                    <div className="edit-outer active">
                      <div className="edit-popup">
                        <Edit
                          handleClose={handleClose}
                          handleSave={handleSave}
                          handleEditCustomer={handleEditCustomer}
                          customer={customer}
                          methods={methods}
                          message={message}
                          editCustomerInProgress={editCustomerInProgress}
                          companyId={companyId}
                        />
                      </div>
                    </div>
                  </>
                ) : ""}
              </div>
              <div className="pagination-outer">
                <Pagination
                  selectedPage={page}
                  onPageChange={getPageData}
                  itemsPerPage={size}
                  totalCount={totalCount}
                  data={customers}
                />
              </div>
            </div>
          </>
        ) : (
          <NoResult
            isLoading={isLoading}
          />
        )}
      </Permission>
    </div>
  );
};

Customers.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.any).isRequired,
  collapsesOpen: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleCollapses: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  handleSearchTerm: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  getPageData: PropTypes.func.isRequired,
  size: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  toggleEditPage: PropTypes.func.isRequired,
  showActive: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleEditCustomer: PropTypes.func.isRequired,
  customer: PropTypes.objectOf(PropTypes.any).isRequired,
  methods: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleAll: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  editCustomerInProgress: PropTypes.bool.isRequired,
  displaySize: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleDropdown: PropTypes.func.isRequired,
  dropdownOpen: PropTypes.bool.isRequired,
  handleSelectedSize: PropTypes.func.isRequired,
  handleSelectedCompany: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  companyIds: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default Customers;
